import React, { useEffect, useRef, useState } from "react";

import { InputNumber as AntInput, InputNumberProps as AntInputNumberProps, FormItemProps } from "antd";
import { RuleObject } from "antd/es/form";

import { TFunction, fractionToPercentage, parseInputRules, useTranslation } from "utils";

import { Form, InputLabel, Typography } from "components";

export type InputNumberProps = Pick<AntInputNumberProps, "decimalSeparator" | "disabled" | "precision" | "step" | "onBlur"> & {
  rules?: RuleObject[];
  validateFirst?: boolean;
  dataTestId?: string;
  className?: string;
  labelClassName?: string;
  defaultValue?: number | null;
  initialValue?: number | null;
  min?: number;
  max?: number;
  value?: number | null;
  label: string | React.ReactNode;
  ariaLabel?: string;
  tooltip?: string;
  message?: string;
  name?: string | string[];
  suffix?: string | React.ReactNode;
  placeholder?: string;
  onChange?: (value: number | null) => void;
  help?: React.ReactNode;
  validateStatus?: FormItemProps["validateStatus"];
  validateTrigger?: string | string[] | false;
  guidanceText?: PracticeManagement.ParameterBounds | string;
};

export const GuidanceTextBox = ({ guidanceText, t }: { guidanceText: PracticeManagement.ParameterBounds | string; t: TFunction }) => {
  if (typeof guidanceText === "string") {
    return (
      <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
        {guidanceText}
      </Typography>
    );
  }

  const isPercentageBounds = guidanceText.every((value) => value <= 1);
  return (
    <Typography variant="paragraph-s" color="grey-600" className="guidance-text">
      {t("helpText.parameterRange", {
        min: isPercentageBounds ? fractionToPercentage(guidanceText[1]) : guidanceText[1],
        max: isPercentageBounds ? `${fractionToPercentage(guidanceText[2])}%` : guidanceText[2],
      })}
    </Typography>
  );
};

export const InputNumber = ({
  className = "",
  labelClassName = "",
  decimalSeparator = ",",
  defaultValue,
  disabled,
  initialValue,
  precision,
  label,
  ariaLabel,
  tooltip,
  message,
  min,
  max,
  name,
  rules,
  validateFirst,
  step,
  suffix,
  value,
  dataTestId,
  placeholder,
  onBlur = () => {},
  onChange = (_value) => {},
  help,
  validateStatus,
  validateTrigger,
  guidanceText,
}: InputNumberProps) => {
  const { t } = useTranslation("misc");
  const inputElement = useRef<HTMLInputElement>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState<number | null | undefined>(initialValue);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasBeenFocused(false);
    onBlur(e);
  };

  const handleChange = (value: number | null) => {
    setInternalValue(value);
    onChange(value);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const active = !!(hasBeenFocused || defaultValue || initialValue || initialValue === 0 || internalValue || internalValue === 0);
  const required = rules && rules[0] && rules[0].required;

  useEffect(() => {
    if (inputElement.current) {
      setInternalValue(inputElement.current.valueAsNumber);
    }
  }, [inputElement]);

  return (
    // Yes, this is gross as fuck, but the problem is we compose too many classes with
    // .floating-input { &.blah-blah }
    // which. means that `flex-1` gets swallowed and the parent keeps the input number
    // shrunk down.
    <div className={className?.includes("flex-1") ? "flex-1" : undefined}>
      <div className={`input-inline-container ${hasBeenFocused ? "active" : ""}`}>
        <Form.Item
          className={`floating-input ${className} ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"}`}
          colon={false}
          label={<InputLabel active={active} required={required} label={label} className={labelClassName} handleClick={handleLabelClick} tooltip={tooltip} />}
          name={name}
          rules={parseInputRules(rules, message)}
          validateFirst={validateFirst ?? false}
          initialValue={initialValue ?? defaultValue}
          help={help}
          validateStatus={validateStatus}
          validateTrigger={validateTrigger}
        >
          <AntInput
            data-testid={dataTestId}
            aria-label={ariaLabel}
            decimalSeparator={decimalSeparator || ","}
            disabled={disabled}
            min={min}
            max={max}
            precision={precision}
            ref={inputElement}
            step={step}
            value={value}
            placeholder={placeholder}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={() => setHasBeenFocused(true)}
          />
        </Form.Item>
        {suffix && (
          <Typography variant="paragraph-m" className="klim-suffix">
            {suffix}
          </Typography>
        )}
      </div>
      {guidanceText ? <GuidanceTextBox t={t} guidanceText={guidanceText} /> : null}
    </div>
  );
};
